import { useEffect, useState } from 'react'
import { api, auth } from '../api/client'
import type { MailCheck, MailConfig } from '../api/types'
import { Card } from '../components/Card'
import { Empty, ErrorBanner, Loading } from '../components/Common'
import { UserAdmin } from '../components/UserAdmin'

/**
 * Administration: what the mail relay is configured to do, whether it can
 * actually do it, and a way to prove it end to end.
 *
 * <p>Mail is the one part of a monitoring system whose failure is silent —
 * nothing tells you the relay was unreachable until the night an alert does
 * not arrive. So the configuration is inspectable and the connection is
 * checkable without sending anything.
 */
export function Admin() {
  const user = auth.user()
  const isAdmin = user?.role === 'admin'

  const [config, setConfig] = useState<MailConfig | null>(null)
  const [check, setCheck] = useState<MailCheck | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>(null)
  const [checking, setChecking] = useState(false)

  const [recipient, setRecipient] = useState('')
  const [sending, setSending] = useState(false)
  const [sendResult, setSendResult] = useState<string | null>(null)
  const [sendError, setSendError] = useState<unknown>(null)

  async function load() {
    setLoading(true)
    try {
      const cfg = await api.mailConfig()
      setConfig(cfg)
      setRecipient((r) => r || cfg.defaultRecipients || '')
      setError(null)
    } catch (e) {
      setError(e)
    } finally {
      setLoading(false)
    }
  }

  async function runCheck() {
    setChecking(true)
    try {
      setCheck(await api.mailCheck())
    } catch (e) {
      setError(e)
    } finally {
      setChecking(false)
    }
  }

  useEffect(() => {
    load().then(runCheck)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function sendTest() {
    setSending(true)
    setSendResult(null)
    setSendError(null)
    try {
      const r = await api.sendTestMail(recipient)
      setSendResult(`Sent to ${r.recipients} in ${r.durationMs} ms.`)
    } catch (e) {
      setSendError(e)
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="stack">
      <div>
        <h1 className="page-title">Administration</h1>
        <p className="page-subtitle">
          Alert email fails silently — nothing reports an unreachable relay
          until the night an alert does not arrive. These checks make that
          visible in advance.
        </p>
      </div>

      <ErrorBanner error={error} />

      <UserAdmin isAdmin={isAdmin} />

      <Card title="Mail settings" prefKey="admin.mailConfig"
            meta={config ? (config.enabled ? 'sending enabled' : 'sending DISABLED') : undefined}>
        {loading && !config ? <Loading what="settings" /> : !config ? (
          <Empty>No mail configuration available.</Empty>
        ) : (
          <>
            {!config.enabled && (
              <div className="metric-note" style={{ marginBottom: 'var(--space-3)' }}>
                <strong>TMMON_MAIL_ENABLED is false.</strong> Alert email will
                not be sent, whatever the settings below say. A test message
                from this page still goes out.
              </div>
            )}
            <table className="table">
              <tbody>
                <Row label="Relay" value={config.host ? `${config.host}:${config.port}` : '— not set —'} />
                <Row label="Transport" value={
                  config.properties?.['mail.smtp.ssl.enable'] === 'true'
                    ? 'implicit TLS (SMTPS)'
                    : config.properties?.['mail.smtp.starttls.enable'] === 'true'
                      ? 'STARTTLS' : 'plain, unencrypted'} />
                <Row label="Authentication" value={
                  config.passwordSet
                    ? `as ${config.username}`
                    : 'none — mail is relayed unauthenticated'} />
                <Row label="From" value={config.from} />
                <Row label="Default recipients" value={config.defaultRecipients || '— none —'} />
                {config.properties?.['mail.smtp.ssl.trust'] ? (
                  <Row label="Certificate trust override"
                       value={String(config.properties['mail.smtp.ssl.trust'])} />
                ) : null}
              </tbody>
            </table>
            <p className="muted" style={{ fontSize: 'var(--text-xs)', marginTop: 'var(--space-3)' }}>
              Set in <code>.env</code>; changing any of it needs{' '}
              <code>docker compose up -d api</code>. The password is never
              returned by the API.
            </p>
          </>
        )}
      </Card>

      <Card
        title="Connection check"
        prefKey="admin.mailCheck"
        meta={
          <button type="button" className="link-button" disabled={checking} onClick={runCheck}>
            {checking ? 'checking…' : 're-run'}
          </button>
        }
        note="Walks DNS, TCP, TLS and credentials in order and reports where it stops. Read-only — it stops short of authenticating and never sends a message."
      >
        {!check ? <Loading what="connection check" /> : (
          <>
            <div
              className="banner"
              style={{
                background: check.reachable ? 'var(--status-ok-bg)' : 'var(--status-critical-bg)',
                borderColor: check.reachable ? 'var(--status-ok-fill)' : 'var(--status-critical-fill)',
                color: check.reachable ? 'var(--status-ok)' : 'var(--status-critical)',
                fontWeight: 600,
              }}
            >
              {check.reachable
                ? 'The relay is reachable and configured. A message can be sent.'
                : `Blocked at "${check.steps[check.steps.length - 1]?.name}" — see below.`}
            </div>
            <table className="table">
              <tbody>
                {check.steps.map((s) => (
                  <tr key={s.name}>
                    <td style={{ width: 26 }}>
                      <span style={{
                        color: s.ok ? 'var(--status-ok)' : 'var(--status-critical)',
                        fontWeight: 700,
                      }} aria-hidden="true">{s.ok ? '✓' : '✗'}</span>
                      <span className="sr-only">{s.ok ? 'passed' : 'failed'}</span>
                    </td>
                    <td style={{ width: 150 }}><strong>{s.name}</strong></td>
                    <td className="mono" style={{ fontSize: 'var(--text-xs)', wordBreak: 'break-word' }}>
                      {s.detail}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </Card>

      <Card title="Send a test email" prefKey="admin.mailTest"
            note="Puts a real message on the wire. This is the only way to settle whether the relay accepts mail — including whether it needs a login at all — and whether SPF and routing let it through.">
        {!isAdmin ? (
          <Empty>Sending a test message requires the admin role.</Empty>
        ) : (
          <>
            <div className="field">
              <label htmlFor="recipient">Recipient</label>
              <input
                id="recipient"
                type="email"
                value={recipient}
                placeholder="you@example.com"
                onChange={(e) => setRecipient(e.target.value)}
              />
              <span className="muted" style={{ fontSize: 'var(--text-xs)' }}>
                Defaults to <code>TMMON_MAIL_TO</code>. Comma-separate for several.
                No credentials are needed to try — many relays accept an
                allowlisted host without a login.
              </span>
            </div>

            <button
              type="button"
              className="btn-primary"
              style={{ width: 'auto' }}
              disabled={sending || !recipient.trim()}
              onClick={sendTest}
            >
              {sending ? 'Sending…' : 'Send test email'}
            </button>

            {sendResult && (
              <div className="banner" style={{
                marginTop: 'var(--space-3)',
                background: 'var(--status-ok-bg)',
                borderColor: 'var(--status-ok-fill)',
                color: 'var(--status-ok)',
              }}>
                {sendResult} If it does not arrive, the relay accepted it and the
                problem is downstream — spam filtering, SPF or DMARC alignment.
              </div>
            )}
            <ErrorBanner error={sendError} />
          </>
        )}
      </Card>
    </div>
  )
}

function Row({ label, value }: { label: string; value?: string | null }) {
  return (
    <tr>
      <td style={{ width: 200 }}><strong>{label}</strong></td>
      <td className="mono" style={{ fontSize: 'var(--text-xs)' }}>{value || '—'}</td>
    </tr>
  )
}
